import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import ReferralCharts from "../components/ReferralCharts";
import PracticeMap from "../components/PracticeMap";

const API = process.env.REACT_APP_API;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function Card({ title, children, className = "" }) {
  return (
    <div className={`p-4 bg-white border border-gray-200 rounded-lg mb-4 ${className}`}>
      <h3 className="font-semibold text-sm mb-2">{title}</h3>
      {children}
    </div>
  );
}

function BackBar({ title, subtitle }) {
  return (
    <div className="mb-4">
      <Link to="/refer/dashboard" className="text-xs text-primary hover:underline">
        ← Back to Dashboard
      </Link>
      <h2 className="text-2xl font-bold text-primary mt-1">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
}

export function ZipAnalytics() {
  const { zip } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/analytics/zip/${zip}`, { headers: authHeaders() })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load analytics"));
  }, [zip]);

  if (error) return <div className="p-6 text-red-600 text-sm">{error}</div>;
  if (!data) return <div className="p-6 text-gray-500 text-sm">Loading ZIP analytics…</div>;

  const pen = data.penetrance || {};

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <BackBar
        title={`ZIP ${data.zip} — Patient Area Analytics`}
        subtitle={`${data.summary.total_events} logged referral${data.summary.total_events !== 1 ? "s" : ""} from patients living here`}
      />

      {pen.top_n > 0 && (
        <Card title={`My Penetrance — top ${pen.top_n} providers officed in ${data.zip}`}>
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className="text-3xl font-bold text-primary">{pen.penetrance_pct != null ? `${pen.penetrance_pct}%` : "—"}</div>
              <div className="text-[11px] text-gray-500">of top {pen.top_n} actively refer to me</div>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="flex h-3 rounded-full overflow-hidden border border-gray-200">
                <div style={{ width: `${(100 * pen.active_in_top) / pen.top_n}%` }} className="bg-emerald-500" title={`${pen.active_in_top} active`} />
                <div style={{ width: `${(100 * pen.prospect_in_top) / pen.top_n}%` }} className="bg-blue-400" title={`${pen.prospect_in_top} prospects`} />
                <div className="flex-1 bg-gray-200" />
              </div>
              <div className="mt-1 text-[11px] text-gray-600">
                {pen.active_in_top} active · {pen.prospect_in_top} prospects · {pen.untapped_in_top} untapped
                <span className="text-gray-400"> · {pen.total_providers_in_zip} providers total in this ZIP</span>
              </div>
            </div>
            <Link to="/refer/radar" className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs">
              Work this area in the Radar →
            </Link>
          </div>
        </Card>
      )}

      <PracticeMap
        patientZips={{ [data.zip]: data.summary.total_events || 1 }}
        providerZips={(() => {
          const byZip = {};
          data.senders.forEach((s) => {
            if (s.zip_code && s.zip_code.length === 5) (byZip[s.zip_code] = byZip[s.zip_code] || []).push(s.name);
          });
          return byZip;
        })()}
      />

      <ReferralCharts summary={data.summary} />

      <Card title={`Who sends me patients from ${data.zip}`}>
        {data.senders.length === 0 ? (
          <div className="text-sm text-gray-400">No referrals logged with this patient ZIP yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2">Referrer</th>
                  <th className="px-3 py-2">Patients</th>
                  <th className="px-3 py-2">Last referral</th>
                  <th className="px-3 py-2">% Commercial</th>
                  <th className="px-3 py-2">Surgical</th>
                  <th className="px-3 py-2">Avg appr.</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.senders.map((s) => (
                  <tr key={s.referrer_id}>
                    <td className="px-3 py-2">
                      <Link to={`/refer/analytics/provider/${s.referrer_id}`} className="font-medium text-primary hover:underline">
                        {s.name}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {s.specialty} · {s.city}, {s.state}
                        {s.organization_name ? ` · ${s.organization_name}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-semibold">{s.count}</td>
                    <td className="px-3 py-2">{s.last_referral_date || "—"}</td>
                    <td className="px-3 py-2">{s.pct_commercial != null ? `${s.pct_commercial}%` : "—"}</td>
                    <td className="px-3 py-2">{s.surgical_count} of {s.count}</td>
                    <td className="px-3 py-2">{s.avg_appropriateness != null ? `${s.avg_appropriateness}/2` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export function ProviderAnalytics() {
  const { referrerId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/analytics/provider/${referrerId}`, { headers: authHeaders() })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load analytics"));
  }, [referrerId]);

  if (error) return <div className="p-6 text-red-600 text-sm">{error}</div>;
  if (!data) return <div className="p-6 text-gray-500 text-sm">Loading provider analytics…</div>;

  const ref = data.referrer;
  const link = data.link;
  const maxZip = Math.max(1, ...data.patient_zips.map((z) => z.count));

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <BackBar
        title={ref.name}
        subtitle={`${ref.specialty || "—"} · ${ref.city || ""}${ref.state ? `, ${ref.state}` : ""} ${ref.zip_code || ""} · NPI ${ref.npi}${ref.organization_name ? ` · 🏥 ${ref.organization_name}${ref.org_member_count ? ` (${ref.org_member_count} providers)` : ""}` : ""}`}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-white border rounded-lg">
          <div className="text-2xl font-bold text-primary">{data.summary.total_events}</div>
          <div className="text-xs text-gray-500">referrals logged{data.last_referral_date ? ` · last ${data.last_referral_date}` : ""}</div>
        </div>
        <div className="p-3 bg-white border rounded-lg">
          <div className="text-sm font-semibold">{link ? (link.status === "active" ? "Active referrer" : link.status === "prospect" ? "Prospect" : "Dormant") : "Not in my list"}</div>
          <div className="text-xs text-gray-500">CRM status</div>
        </div>
        <div className="p-3 bg-white border rounded-lg">
          <div className="text-sm font-semibold">{data.ever_called ? "Yes" : "No"}</div>
          <div className="text-xs text-gray-500">called before</div>
        </div>
        <div className="p-3 bg-white border rounded-lg">
          <div className="text-sm">{ref.phone && <div>{ref.phone} <span className="text-gray-400">(practice)</span></div>}{ref.mobile_phone && <div>{ref.mobile_phone} <span className="text-gray-400">(mobile)</span></div>}{!ref.phone && !ref.mobile_phone && "—"}</div>
          <div className="text-xs text-gray-500">phone</div>
        </div>
      </div>

      <PracticeMap
        patientZips={Object.fromEntries(data.patient_zips.map((z) => [z.zip, z.count]))}
        providerZips={ref.zip_code && String(ref.zip_code).slice(0, 5).length === 5 ? { [String(ref.zip_code).slice(0, 5)]: [ref.name] } : {}}
      />

      <ReferralCharts summary={data.summary} />

      <Card title="Where their patients come from">
        {data.patient_zips.length === 0 ? (
          <div className="text-sm text-gray-400">No patient ZIPs logged for this referrer yet.</div>
        ) : (
          <div className="space-y-1.5">
            {data.patient_zips.map((z) => (
              <div key={z.zip} className="flex items-center gap-2 text-xs">
                <Link to={`/refer/analytics/zip/${z.zip}`} className="w-14 font-semibold text-primary hover:underline">{z.zip}</Link>
                <div className="flex-1 h-3 bg-gray-100 rounded overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${(100 * z.count) / maxZip}%`, background: "#32613D", opacity: 0.75 }} />
                </div>
                <span className="w-12 text-gray-600">{z.count} pt{z.count !== 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Call history">
        {data.call_history.length === 0 ? (
          <div className="text-sm text-gray-400">Never added to a call list or outreach plan.</div>
        ) : (
          <div className="space-y-1.5 text-sm">
            {data.call_history.map((c, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${c.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                  {c.status}
                </span>
                <span className="text-gray-700">{c.plan}</span>
                <span className="text-gray-400 text-xs">added {c.added_at ? c.added_at.slice(0, 10) : "—"}</span>
                {c.notes && <span className="text-gray-500 text-xs">· {c.notes}</span>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {link?.notes && (
        <Card title="My notes">
          <div className="text-sm text-gray-700 whitespace-pre-wrap">{link.notes}</div>
        </Card>
      )}
    </div>
  );
}
